import React, { useState, useEffect, useRef } from "react";
import { formatText } from "../TruncateParties";
import { IoGrid, IoDocumentTextOutline, IoCloudUploadOutline, IoTrashOutline, IoEyeOutline, IoClose } from "react-icons/io5";
import { FaTableList, FaGavel, FaCalendarDays, FaFilePdf } from "react-icons/fa6";
import { addOrders, getOrdersByCourtRef, deleteOrderById } from "../../services/api/OrderApi";
import { uploadFileWithProgress } from "../../utils/ftpUploadUtils";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteConfirmationModal";
import "./RenderOrders.scss";


export const RenderOrders = ({ orders, courtRef, isSupremeCourt }) => {
  const { showToast } = useToast();
  const { company, role } = useAuth();
  const [viewMode, setViewMode] = useState("table");
  const [manualOrders, setManualOrders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (courtRef) {
      fetchManualOrders();
    }
  }, [courtRef]);

  const fetchManualOrders = async () => {
    try {
      const res = await getOrdersByCourtRef(courtRef);
      setManualOrders(res.data || []);
    } catch (err) {
      console.error("Failed to fetch manual orders:", err);
    }
  };

  const parseOrderDate = (dateStr) => {
    if (!dateStr) return null;
    const ddmmyyyy = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (ddmmyyyy) return new Date(`${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`);
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const sortedOrders = orders
    ? [...orders].sort((a, b) => {
      const dateA = parseOrderDate(a.orderDate);
      const dateB = parseOrderDate(b.orderDate);
      if (dateA && dateB) return dateB - dateA;
      return Number(b.orderNumber) - Number(a.orderNumber);
    })
    : [];

  const hasValidPdf = (orderPdfLink) => {
    return (
      orderPdfLink &&
      orderPdfLink.trim() !== "" &&
      !orderPdfLink.startsWith("https://")
    );
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return "Document";
    // Try to split by 'others/' first as requested
    const parts = url.split('/others/');
    if (parts.length > 1) {
      return parts[1];
    }
    // Fallback to just taking the last part of the path
    const slashParts = url.split('/');
    return slashParts[slashParts.length - 1] || "Document";
  };

  const handlePdfClick = (orderPdfLink) => {
    window.open(
      `${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${orderPdfLink}`,
      "_blank"
    );
  };

  const handleOpenModal = (orderNumber) => {
    setSelectedOrderNumber(orderNumber);
    setSelectedFile(null);
    setUploadProgress(0);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOrderNumber(null);
    setSelectedFile(null);
    setUploading(false);
  };

  const validateFile = (file) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const name = file.name.toLowerCase();
    const isAllowedExt = name.endsWith('.pdf') || name.endsWith('.doc') || name.endsWith('.docx');
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type) && !isAllowedExt) {
      return { valid: false, error: "Invalid file type. Only PDF, DOC, and DOCX are allowed." };
    }

    if (file.size > maxSize) {
      return { valid: false, error: "File size exceeds 5MB limit." };
    }

    return { valid: true };
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validation = validateFile(file);
      
      if (!validation.valid) {
        showToast(validation.error, "error");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      
      setSelectedFile(file);
      handleUpload(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const validation = validateFile(file);
      
      if (!validation.valid) {
        showToast(validation.error, "error");
        return;
      }
      
      setSelectedFile(file);
      handleUpload(file);
    }
  };

  const handleUpload = async (fileParam) => {
    const fileToUse = (fileParam && fileParam.name) ? fileParam : selectedFile;
    if (!fileToUse || !courtRef || !selectedOrderNumber) return;

    setUploading(true);
    try {
      const uploadResult = await uploadFileWithProgress(
        fileToUse,
        `${company?._id}/others`,
        (progress) => setUploadProgress(progress)
      );

      if (uploadResult.success) {
        // Note: uploadResult.imageUrl contains the relative path identifier used for retrieval
        // It is named imageUrl in the utility but works for all file types
        const payload = {
          courtRef: courtRef,
          orderNumber: selectedOrderNumber,
          orderUrl: uploadResult.imageUrl 
        };

        await addOrders(payload);
        await fetchManualOrders();
        setSelectedFile(null);
        setUploadProgress(0);
        if (fileInputRef.current) fileInputRef.current.value = "";
        showToast("Order uploaded successfully", "success");
      }
    } catch (err) {
      console.error("Upload failed:", err);
      showToast("Failed to upload order.", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteOrder = (orderId) => {
    setOrderToDelete(orderId);
    setShowDeleteModal(true);
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;
    
    try {
      await deleteOrderById(orderToDelete);
      await fetchManualOrders();
      showToast("Order deleted successfully", "success");
    } catch (err) {
      console.error("Delete failed:", err);
      showToast("Failed to delete order.", "error");
    } finally {
      setShowDeleteModal(false);
      setOrderToDelete(null);
    }
  };

  const getManualOrdersForNumber = (orderNumber) => {
    if (!orderNumber) return [];
    return manualOrders.filter(o => String(o.orderNumber) === String(orderNumber));
  };

  // --- Renderers ---

  // Kept exactly as per your request (No changes to Table View)
  const renderTableView = () => (
    <div className="table-responsive render-orders-container">
      <table className="documents-table">
        <thead>
          <tr>
            <th className="td-th-casedet">Order No</th>
            <th className="td-th-casedet">Order Date</th>
            <th className="td-th-casedet">Order On</th>
            <th className="td-th-casedet">Judge</th>
            <th className="td-th-casedet">Document</th>
            <th className="td-th-casedet">{role === "customer" ? "View Uploaded Orders" : "Upload/View Orders"}</th>
          </tr>
        </thead>
        <tbody>
          {sortedOrders.map((order, index) => (
            <tr key={index}>
              <td className="td-th-casedet">{isSupremeCourt ? sortedOrders.length - index : order.orderNumber}</td>
              <td className="td-th-casedet">{order.orderDate}</td>
              <td className="td-th-casedet">{order.orderOn}</td>
              <td style={{ maxWidth: "300px", minWidth: "150px" }} className="td-th-casedet">
                <div
                  style={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                  }}
                  title={order.judge}
                >
                  {formatText(order.judge)}
                </div>
              </td>
              <td className="td-th-casedet">
                {hasValidPdf(order.orderPdfLink) ? (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePdfClick(order.orderPdfLink);
                    }}
                    style={{
                      cursor: "pointer",
                      color: "var(--primary-color)",
                      fontWeight: "500",
                      textDecoration: "none",
                    }}
                  >
                    View PDF
                  </a>
                ) : (
                  <span
                    style={{
                      color: "#dc3545",
                      fontStyle: "italic",
                      fontSize: "0.9em",
                      display: "block",
                      whiteSpace: "normal",
                      minWidth: "180px",
                    }}
                  >
                    This order is not uploaded on source
                  </span>
                )}
              </td>
              <td className="td-th-casedet">
                {role === "customer" ? (
                  getManualOrdersForNumber(order.orderNumber).length > 0 ? (
                    <button 
                      className="upload-btn"
                      onClick={() => handleOpenModal(order.orderNumber)}
                    >
                      View Orders
                    </button>
                  ) : (
                    <div style={{ 
                      textAlign: 'left',
                      paddingLeft: '12px',
                      width: '100%',
                      fontSize: '1.2em',
                      color: '#6b7280'
                    }}>
                      —
                    </div>
                  )
                ) : (
                  <button 
                    className="upload-btn"
                    onClick={() => handleOpenModal(order.orderNumber)}
                  >
                    Upload / View Orders
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Updated Grid View to show ALL details in a Card
  const renderGridView = () => (
    <div className="orders-grid-layout render-orders-container">
      {sortedOrders.map((order, index) => {
        const hasPdf = hasValidPdf(order.orderPdfLink);

        return (
          <div key={index} className="modern-order-card">
            {/* 1. Header: Order Number and Date */}
            <div className="card-header-strip">
              <div className="order-badge">
                <IoDocumentTextOutline /> Order #{isSupremeCourt ? sortedOrders.length - index : order.orderNumber}
              </div>
              <div className="order-date">
                {order.orderDate}
              </div>
            </div>

            {/* 2. Body: Order On and Judge */}
            <div className="card-content-body">
              <div className="detail-row">
                <div className="detail-label">
                  <FaCalendarDays className="row-icon" /> Order On
                </div>
                <div className="detail-value">{order.orderOn}</div>
              </div>

              <div className="detail-row">
                <div className="detail-label">
                  <FaGavel className="row-icon" /> Judge
                </div>
                <div className="detail-value judge-text" title={order.judge}>
                  {formatText(order.judge)}
                </div>
              </div>
            </div>

            {/* 3. Footer: Document Action */}
            <div className="card-footer-action">
              {hasPdf ? (
                <button
                  className="pdf-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePdfClick(order.orderPdfLink);
                  }}
                >
                  <FaFilePdf /> View PDF
                </button>
              ) : (
                <div className="no-doc-status">
                  This order is not uploaded on source
                </div>
              )}
              
              {role === "customer" ? (
                getManualOrdersForNumber(order.orderNumber).length > 0 ? (
                  <button 
                    className="upload-btn"
                    style={{marginLeft: '10px'}}
                    onClick={() => handleOpenModal(order.orderNumber)}
                  >
                    View Orders
                  </button>
                ) : null
              ) : (
                <button 
                  className="upload-btn"
                  style={{marginLeft: '10px'}}
                  onClick={() => handleOpenModal(order.orderNumber)}
                >
                  Upload / View Orders
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="tab-content render-orders-container">
      <div className="summary header-actions-container">
        <div className="sum-amount">
          <p className="margin-zero exp-count">
            <strong>Total Orders - </strong> {orders ? orders.length : 0}
          </p>
        </div>

        <div className="actions-right">
          {orders && orders.length > 0 && (
            <div className="view-toggles">
              <button
                className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                title="Grid View"
              >
                <IoGrid />
              </button>
              <button
                className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
                onClick={() => setViewMode("table")}
                title="Table View"
              >
                <FaTableList />
              </button>
            </div>
          )}
        </div>
      </div>

      {orders && orders.length === 0 ? (
        <div className="no-data-container">
          <h3 className="no-data-title">No Orders Found</h3>
          <p className="no-data-message">
            No court orders are available for this case yet.
          </p>
        </div>
      ) : viewMode === "table" ? (
        renderTableView()
      ) : (
        renderGridView()
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manage Orders for Order No: {selectedOrderNumber}</h3>
              <button className="close-icon-btn" onClick={handleCloseModal}>
                <IoClose size={24} />
              </button>
            </div>
            
            {role !== "customer" && (
              <div className="upload-section">
                <div 
                  className={`drag-drop-area ${dragActive ? 'active' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input 
                    type="file" 
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    id="order-file-upload"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  />
                  <label htmlFor="order-file-upload" className="upload-label">
                    <IoCloudUploadOutline size={48} className="upload-icon" />
                    <p>Click to select or drag and drop</p>
                    <small>PDF, DOC, DOCX (Max 5MB)</small>
                  </label>
                </div>

                {selectedFile && (
                  <div className="selected-file-info">
                    <div className="file-details">
                      <IoDocumentTextOutline className="file-icon" />
                      <span className="file-name">{selectedFile.name}</span>
                      <span className="file-size">({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                    </div>
                    <button 
                      className="upload-submit-btn"
                      onClick={() => handleUpload()}
                      disabled={uploading}
                    >
                      {uploading ? `Uploading... ${Math.round(uploadProgress)}%` : "Upload Document"}
                    </button>
                  </div>
                )}
                
                {uploading && (
                  <div className="progress-bar-container">
                    <div 
                      className="progress-bar-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="uploaded-orders-list">
              <h4>Uploaded Orders</h4>
              {getManualOrdersForNumber(selectedOrderNumber).length === 0 ? (
                <div className="no-docs-message">
                  <p>No manually uploaded orders found.</p>
                </div>
              ) : (
                <div className="docs-list">
                  {getManualOrdersForNumber(selectedOrderNumber).map((order, idx) => (
                    <div key={order._id || idx} className="order-item">
                      <div className="doc-info">
                        <IoDocumentTextOutline className="doc-icon" />
                        <span title={getFileNameFromUrl(order.orderUrl)}>{getFileNameFromUrl(order.orderUrl)}</span>
                        <span className="upload-date">
                          {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : ''}
                        </span>
                      </div>
                      <div className="order-actions">
                        <button 
                          className="view-btn"
                          onClick={() => handlePdfClick(order.orderUrl)}
                          title="View Document"
                        >
                          <IoEyeOutline /> View
                        </button>
                        {role !== "customer" && (
                          <button 
                            className="delete-btn"
                            onClick={() => handleDeleteOrder(order._id)}
                            title="Delete Document"
                          >
                            <IoTrashOutline /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        itemType="Order Document"
        onConfirm={confirmDeleteOrder}
        onCancel={() => {
          setShowDeleteModal(false);
          setOrderToDelete(null);
        }}
      />
    </div>
  );
};